// Every shared-cache key in one place, so the code that changes data can
// refresh exactly the lists it affects without importing the hooks that read them.
export const queryKeys = {
  users: {
    // The signed-in user's profile (/v2/users/me/).
    me: () => ['users', 'me'] as const,
  },
  organizations: {
    all: ['organizations'] as const,
    allPages: () => [...queryKeys.organizations.all, 'all-pages'] as const,
    // One page of the Workspaces table.
    page: (query: object) =>
      [...queryKeys.organizations.all, 'page', query] as const,
    // One workspace and its members. Under `all`, so every workspace or
    // member save refreshes it.
    detail: (orgId: string) =>
      [...queryKeys.organizations.all, 'detail', orgId] as const,
    // A workspace picker's server-side search.
    search: (term: string) =>
      [...queryKeys.organizations.all, 'search', term] as const,
  },
  routes: {
    all: ['routes'] as const,
    logs: (routeId: string) =>
      [...queryKeys.routes.all, routeId, 'logs'] as const,
    list: (query: object) => [...queryKeys.routes.all, 'list', query] as const,
    // The Route page's own record, addressed by the URL's :route_id.
    detail: (routeId: string) =>
      [...queryKeys.routes.all, routeId, 'detail'] as const,
    sources: (routeId: string, providerId: string, query: object) =>
      [...queryKeys.routes.all, routeId, 'sources', providerId, query] as const,
    // A route record's field-mapping configuration.
    configuration: (recordId: string) =>
      [...queryKeys.routes.all, 'configuration', recordId] as const,
    // The Routes that send data back into a provider (bidirectional flows).
    reverse: (providerId: string) =>
      [...queryKeys.routes.all, 'reverse', providerId] as const,
    // The Routes a provider integration feeds (/v2/routes/?provider=), shared
    // by the Route page and the Connection page.
    byProvider: (providerId: string) =>
      [...queryKeys.routes.all, 'by-provider', providerId] as const,
  },
  sources: {
    all: ['sources'] as const,
    list: (query: object) => [...queryKeys.sources.all, 'list', query] as const,
  },
  connections: {
    all: ['connections'] as const,
    list: (query: object) =>
      [...queryKeys.connections.all, 'list', query] as const,
    // Which connections each Route uses as its source or destination.
    roleMap: () => [...queryKeys.connections.all, 'role-map'] as const,
    // The candidates a Route's add-destination drawer offers.
    drawer: (query: object) =>
      [...queryKeys.connections.all, 'drawer', query] as const,
    // EarthRanger rows' account details, which the list endpoint omits.
    auth: (ids: string[]) =>
      [...queryKeys.connections.all, 'auth', ids] as const,
    // Everything the Connection page loads for one record, so a save can
    // refresh all of it at once.
    byId: (id: string) => [...queryKeys.connections.all, 'record', id] as const,
    detail: (id: string) =>
      [...queryKeys.connections.byId(id), 'detail'] as const,
    // Routes this record receives data from.
    routesAsDestination: (id: string, query: object) =>
      [
        ...queryKeys.connections.byId(id),
        'routes-as-destination',
        query,
      ] as const,
    // Routes this record provides data to.
    providerRole: (id: string) =>
      [...queryKeys.connections.byId(id), 'provider-role'] as const,
    // Routes this record could be added to as a destination.
    routeCandidates: (id: string, search: string, workspaceId: string) =>
      [
        ...queryKeys.connections.byId(id),
        'route-candidates',
        search,
        workspaceId,
      ] as const,
  },
  filterOptions: {
    destinationUrls: ['filter-options', 'destination-urls'] as const,
    // Connections lists every technology; everywhere else only the ones
    // that can receive data.
    destinationTypes: (allTypes: boolean) =>
      [
        'filter-options',
        'destination-types',
        allTypes ? 'all' : 'push',
      ] as const,
    providerTypes: ['filter-options', 'provider-types'] as const,
  },
  // Options for `gundi:reference` fields, keyed by the caller's own cache key.
  referenceOptions: {
    all: ['reference-options'] as const,
    entry: (key: string) => [...queryKeys.referenceOptions.all, key] as const,
    // For target: "provider" / "destination" fields: integration details and
    // the targets resolved for an integration, keyed by direction.
    providerDetails: (providerId: string) =>
      [
        ...queryKeys.referenceOptions.all,
        'provider-details',
        providerId,
      ] as const,
    providerTargets: (key: string) =>
      [...queryKeys.referenceOptions.all, 'provider-targets', key] as const,
  },
  // The Add Route flow's technology search (API types merged with Gundi v1 ones).
  newRouteProviderTypes: (search: string) =>
    ['new-route-provider-types', search] as const,
  // The technology the Add Route flow's second step was opened for.
  newRouteProviderType: (value: string) =>
    ['new-route-provider-type', value] as const,
};
